import {
  useGetStatsQuery,
  useUpdateProfileMutation,
} from "@/chat-platform/api/nebula-stats/nebulaStatsApi";
import { formatNumber } from "@/utils/formatUtils";
import { getApiErrorMessage } from "@/utils/api";
import { showError, showSuccess } from "@/utils/toastUtils";
import { motion } from "framer-motion";
import React from "react";
import AppSelect from "../../../../components/AppSelect";
import { PORTFOLIO_TYPE } from "../../utils/nebulaData";
import {
  buildProfileValues,
  getChangedProfileFields,
  isFeatureLocked,
  validateProfile,
} from "../../utils/nebulaUtils";
import { CloseIcon } from "../icons/CloseIcon";
import LockFeatureComponent from "./LockFeatureComponent";
import { NotePencilIcon } from "../icons/NotePencilIcon";
import { WarningTriangleFilledDotIcon } from "../icons/WarningTriangleFilledDotIcon";
import Spinner from "../Spinner";
import SettingItem from "./SettingItem";
import SettingsSection from "./SettingsSection";
import SettingToggle from "./SettingToggle";

const EXPERIENCE_OPTIONS = [
  { value: "0", label: "Nhà đầu tư mới" },
  { value: "1", label: "Nhà đầu tư chủ động" },
  { value: "2", label: "Trader chuyên nghiệp" },
];

export default function PortfolioAssistantSettings({ onBack }) {
  const { data: statsData, isLoading: isFetching } = useGetStatsQuery({
    type: PORTFOLIO_TYPE.PAPER,
  });
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  const apiProfile = statsData?.data?.profile;
  const tier = statsData?.data?.rule?.tier || "FREE";

  const [values, setValues] = React.useState(() => buildProfileValues());
  const [isEditingInfo, setIsEditingInfo] = React.useState(false);

  const lockedUnless = (key, field, unlockValue = true) =>
    isFeatureLocked(tier, key) && apiProfile?.[field] !== unlockValue;

  const errors = validateProfile(values, tier);
  const hasErrors = Object.keys(errors).length > 0;

  React.useEffect(() => {
    if (apiProfile) setValues(buildProfileValues(apiProfile));
  }, [apiProfile]);

  const setField = (field, value) =>
    setValues((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "marginEnabled" && value) next.hedgingEnabled = false;
      if (field === "hedgingEnabled" && value) next.marginEnabled = false;
      return next;
    });

  const handleReset = () => {
    if (apiProfile) setValues(buildProfileValues(apiProfile));
  };

  const handleCancelInfo = () => {
    handleReset();
    setIsEditingInfo(false);
  };

  const handleSaveInfo = () => {
    if (!hasErrors) setIsEditingInfo(false);
  };

  const handleSave = async () => {
    if (hasErrors) {
      showError(errors.capitalAmount);
      return;
    }
    try {
      const changedFields = getChangedProfileFields(values, apiProfile);
      await updateProfile(changedFields).unwrap();
      showSuccess("Lưu thay đổi thành công");
      onBack();
    } catch (error) {
      showError(getApiErrorMessage(error, "Lưu thay đổi thất bại"));
    }
  };

  const hasChanges =
    apiProfile &&
    Object.keys(getChangedProfileFields(values, apiProfile)).length > 0;

  const isModelChanged = values.strategyModel !== apiProfile?.strategyModel;

  const sections = [
    {
      key: "model",
      title: "CHỌN MODEL",
      noTopDivider: true,
      rows: [
        {
          title: "AI Model",
          locked: lockedUnless(
            "strategyModel:horizon",
            "strategyModel",
            "horizon",
          ),
          tooltip:
            "Mô hình AI Nebula sử dụng để chấm điểm và xếp hạng cổ phiếu, làm cơ sở lựa chọn danh mục cho bạn.",
          subtitle: "Chọn chiến lược phù hợp với bạn",
          forceRow: true,
          control: {
            type: "select",
            value: values.strategyModel,
            options: [
              {
                value: "orbit",
                label: "Nebula Orbit",
                subtitle:
                  "Chiến lược cân bằng giữa cơ bản và kỹ thuật. Tần suất quay vòng vốn ở mức trung bình.",
              },
              {
                value: "horizon",
                label: "Nebula Horizon",
                locked: lockedUnless(
                  "strategyModel:horizon",
                  "strategyModel",
                  "horizon",
                ),
                subtitle:
                  "Chiến lược chủ động và thiên nhiều hơn về phân tích kỹ thuật. Tần suất quay vòng vốn cao.",
              },
            ],
            onChange: (value) => setField("strategyModel", value),
            className: "min-w-[178px]",
            dropdownWidth: "275px",
            suffixIcon: isModelChanged ? (
              <WarningTriangleFilledDotIcon
                color="#BD6600"
                className="ml-[8px] shrink-0"
              />
            ) : null,
          },
          below: isModelChanged && (
            <div className="mx-5 mt-2 mb-4 p-3 bg-[#fbbf240d] border border-[#fde5a7] rounded-xl flex flex-col gap-3">
              <div className="flex gap-2 items-start">
                <div className="mt-0.5 shrink-0">
                  <WarningTriangleFilledDotIcon color="#BD6600" />
                </div>
                <span className="text-[12px] leading-[18px] text-[#8e4d00]">
                  Lưu ý: Đổi model sẽ tạo lại toàn bộ danh mục.
                </span>
              </div>
            </div>
          ),
        },
        {
          title: "Rổ cổ phiếu",
          locked: lockedUnless(
            "stockUniverse:pool_100",
            "stockUniverse",
            "pool_100",
          ),
          tooltip:
            "Tập hợp cổ phiếu mà Nebula được phép lựa chọn khi xây dựng danh mục cho bạn.",
          subtitle: "Chọn rổ phù hợp với khẩu vị rủi ro của bạn",
          forceRow: true,
          control: {
            type: "select",
            value: values.stockUniverse,
            options: [
              { value: "pool_50", label: "Cổ phiếu Bluechip" },
              {
                value: "pool_100",
                label: "Bluechip & Midcap",
                locked: lockedUnless(
                  "stockUniverse:pool_100",
                  "stockUniverse",
                  "pool_100",
                ),
              },
            ],
            onChange: (value) => setField("stockUniverse", value),
            className: "min-w-[178px]",
          },
        },
      ],
    },
    {
      key: "asset",
      title: "LOẠI TÀI SẢN",
      subtitle: "Lựa chọn loại tài sản bạn muốn Nebula quản lý",
      rows: [
        {
          title: (
            <>
              Cổ phiếu <span className="text-[#636369]">(mặc định)</span>
            </>
          ),
          iconColor: "#B9A2FB",
          control: {
            type: "toggle",
            field: "activateStocks",
            disabled: true,
            forceActive: true,
          },
        },
        {
          title: "Vàng",
          iconColor: "#FCCC50",
          locked: lockedUnless("activateGold", "activateGold"),
          control: {
            type: "toggle",
            field: "activateGold",
            locked: lockedUnless("activateGold", "activateGold"),
          },
        },
        {
          title: "Trái phiếu",
          iconColor: "#3389F0",
          locked: lockedUnless("activateFixedIncome", "activateFixedIncome"),
          control: {
            type: "toggle",
            field: "activateFixedIncome",
            locked: lockedUnless("activateFixedIncome", "activateFixedIncome"),
          },
        },
        {
          title: "Crypto",
          iconColor: "#F09933",
          locked: lockedUnless("activateCrypto", "activateCrypto"),
          control: {
            type: "toggle",
            field: "activateCrypto",
            locked: lockedUnless("activateCrypto", "activateCrypto"),
          },
        },
      ],
    },
    {
      key: "feature",
      title: "TÍNH NĂNG NÂNG CAO",
      subtitle:
        "Chỉ dành cho nhà đầu tư chuyên nghiệp và có khả năng chấp nhận rủi ro cao",
      rows: [
        {
          title: "Giao dịch ký quỹ",
          locked: lockedUnless("marginEnabled", "marginEnabled"),
          tooltip:
            "Nebula vay ký quỹ để tăng sức mua. Khoản vay chịu lãi suất tính theo ngày và bị giới hạn bởi trần vay.",
          subtitle: "Cho phép Nebula sử dụng nợ vay margin làm đòn bẩy",
          alignTop: true,
          control: {
            type: "toggle",
            field: "marginEnabled",
            locked: lockedUnless("marginEnabled", "marginEnabled"),
          },
          below: values.marginEnabled && (
            <div className="px-5 pb-4">
              <div className="bg-[#F5F5F5] rounded-xl p-3 flex flex-col gap-2">
                {[
                  {
                    label: "Lãi suất margin",
                    value: `${apiProfile?.marginRate ?? 13.5}%/năm`,
                  },
                  {
                    label: "Giới hạn margin",
                    value: `${apiProfile?.marginLimitPct ?? 50}% NAV`,
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex justify-between items-center"
                  >
                    <span className="text-[12px] text-[#636369]">
                      {item.label}
                    </span>
                    <span className="text-[12px] font-medium text-[#111827]">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ),
        },
        {
          title: "Hedging rủi ro",
          locked: lockedUnless("hedgingEnabled", "hedgingEnabled"),
          tooltip:
            "Nebula mở vị thế phái sinh VN30F1M để giảm mức thua lỗ khi thị trường chung suy giảm.",
          subtitle:
            "Cho phép Nebula sử dụng hợp đồng phái sinh (VN30 Futures) để phòng ngừa rủi ro",
          alignTop: true,
          control: {
            type: "toggle",
            field: "hedgingEnabled",
            locked: lockedUnless("hedgingEnabled", "hedgingEnabled"),
          },
          below: (
            <p className="mx-[20px] mb-[24px] mt-[6px] text-[12px] italic text-[#636369] leading-[18px]">
              <span className="text-[#111827]">{`Chú ý: `}</span>
              Bạn chỉ có thể bật 1 trong 2 tính năng margin hoặc hedging.
            </p>
          ),
        },
      ],
    },
    {
      key: "info",
      title: "THÔNG TIN",
      noBorder: true,
      titleClassName: "mb-2",
      contentClassName: "flex flex-col gap-[8px] px-5",
      isInfo: true,
      rows: [
        {
          label: "Tổng vốn",
          display: `${formatNumber(values.capitalAmount)} đ`,
          control: {
            type: "input",
            value: formatNumber(values.capitalAmount),
            onChange: (e) =>
              setField(
                "capitalAmount",
                parseInt(e.target.value.replace(/\D/g, ""), 10) || 0,
              ),
            error: errors.capitalAmount,
          },
        },
        {
          label: "Kinh nghiệm đầu tư",
          display:
            EXPERIENCE_OPTIONS.find(
              (o) => o.value === String(values.investingExperience),
            )?.label ?? EXPERIENCE_OPTIONS[2].label,
          control: {
            type: "select",
            value: String(values.investingExperience),
            options: EXPERIENCE_OPTIONS,
            onChange: (value) => setField("investingExperience", Number(value)),
            className: "w-[178px]",
          },
        },
        {
          label: "Ngày tạo",
          display: apiProfile?.createdDate ?? "--",
        },
      ],
    },
  ];

  const renderControl = (control) => {
    switch (control.type) {
      case "select":
        return (
          <AppSelect
            value={control.value}
            options={control.options}
            onChange={(v) => {
              if (control.options.find((o) => o.value === v)?.locked) return;
              control.onChange(v);
            }}
            className={control.className}
            dropdownWidth={control.dropdownWidth}
            suffixIcon={control.suffixIcon}
            placement="right"
          />
        );
      case "toggle":
        return (
          <div className="flex items-center gap-2">
            {control.locked && <LockFeatureComponent className="shrink-0" />}
            <SettingToggle
              active={control.forceActive ? true : values[control.field]}
              onChange={(v) => setField(control.field, v)}
              disabled={control.disabled || control.locked}
            />
          </div>
        );
      case "input":
        return (
          <div className="flex flex-col items-end gap-1">
            <input
              autoFocus
              type="text"
              value={control.value}
              onChange={control.onChange}
              className={`w-[178px] h-8 px-3 text-[14px] font-medium text-[#111827] border rounded-lg outline-none ${
                control.error
                  ? "border-[#FF6B6B] hover:border-[#FF6B6B] focus:border-[#FF6B6B]"
                  : "border-[#ECECED] hover:border-[#C5C5C7] focus:border-[#C5C5C7]"
              }`}
            />
            {control.error && (
              <span className="text-[12px] text-[#FF6B6B] text-right w-[178px]">
                {control.error}
              </span>
            )}
          </div>
        );
      case "text":
      default:
        return (
          <span className="text-[14px] font-medium text-[#636369]">
            {control.value}
          </span>
        );
    }
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="absolute inset-0 bg-white z-30 flex flex-col"
    >
      <div className="flex items-center justify-between h-12 px-5 bg-white border-b border-[#ECECED] shrink-0">
        <h2 className="text-[16px] font-bold text-[#111827]">Cài đặt Nebula</h2>
        <button
          onClick={onBack}
          className="w-8 h-8 rounded-full border border-[#ECECED] flex items-center justify-center text-[#3C3C43] hover:bg-gray-50 transition-colors shadow-sm"
        >
          <CloseIcon size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scroll-smooth thin-scrollbar">
        {isFetching && (
          <div className="flex items-center justify-center h-full">
            <Spinner size={24} />
          </div>
        )}
        {!isFetching && (
          <>
            {sections.map((section) => (
              <SettingsSection
                key={section.key}
                data-tour-id={
                  section.key === "asset" ? "settings-asset" : undefined
                }
                title={section.title}
                subtitle={section.subtitle}
                noTopDivider={section.noTopDivider}
                noBorder={section.noBorder}
                titleClassName={section.titleClassName}
                titleSuffix={
                  section.isInfo &&
                  !isEditingInfo && (
                    <button
                      onClick={() => setIsEditingInfo(true)}
                      className="flex items-center justify-center"
                    >
                      <NotePencilIcon size={20} />
                    </button>
                  )
                }
                headerRight={
                  section.isInfo &&
                  isEditingInfo && (
                    <div className="flex items-center gap-2">
                      <button
                        className="text-[12px] font-medium text-[#636369]"
                        onClick={handleCancelInfo}
                      >
                        Hủy
                      </button>
                      <button
                        className="text-[12px] font-bold text-[#111827] disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleSaveInfo}
                        disabled={hasErrors}
                      >
                        Lưu
                      </button>
                    </div>
                  )
                }
              >
                <div className={section.contentClassName}>
                  {section.rows.map((row, index) => (
                    <React.Fragment key={row.title ?? row.label ?? index}>
                      {section.isInfo && (
                        <div className="flex items-center justify-between">
                          <span className="text-[14px] text-[#636369]">
                            {row.label}
                          </span>
                          {isEditingInfo &&
                            row.control &&
                            renderControl(row.control)}
                          {!(isEditingInfo && row.control) && (
                            <span className="text-[14px] font-medium text-[#111827]">
                              {row.display}
                            </span>
                          )}
                        </div>
                      )}
                      {!section.isInfo && (
                        <SettingItem
                          data-tour-id={
                            row.control?.field === "hedgingEnabled"
                              ? "settings-hedging"
                              : row.title === "AI Model"
                                ? "settings-model"
                                : undefined
                          }
                          title={row.title}
                          titleSuffix={
                            row.locked &&
                            row.control?.type !== "toggle" && (
                              <LockFeatureComponent className="shrink-0" />
                            )
                          }
                          subtitle={row.subtitle}
                          tooltip={row.tooltip}
                          iconColor={row.iconColor}
                          forceRow={row.forceRow}
                          alignTop={row.alignTop}
                          rightContent={renderControl(row.control)}
                        />
                      )}
                      {row.below}
                    </React.Fragment>
                  ))}
                </div>
              </SettingsSection>
            ))}

            <div className="p-5 flex gap-3 mt-4">
              <button
                onClick={handleReset}
                disabled={!hasChanges || isUpdating}
                className="flex-1 h-11 rounded-xl bg-[#F5F5F5] text-[#111827] text-[16px] font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Đặt lại
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || isUpdating}
                className="flex-1 h-11 rounded-xl bg-[#3C3C43] text-white text-[16px] font-medium hover:bg-[#2C2C30] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? <Spinner size={20} /> : "Lưu thay đổi"}
              </button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
